import React, { useState } from 'react';

const PROPERTY_API_URL = 'https://property-lookup-api.anassk.workers.dev';
const PROPERTY_API_KEY = '19285196e984c67c9c96784becbd3464e692815a4393e4c948be460a2019182e';

function formatCurrency(value) {
  if (!value) return '—';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

function formatNumber(value) {
  if (!value) return '—';
  return value.toLocaleString();
}

function PropertyModal({
  property,
  onClose,
  onAddToCRM,
  onLookupDetails,
  onGenerateFlyer,
  isInDB,
  dbLead
}) {
  const [loading, setLoading] = useState(false);
  const [lookupData, setLookupData] = useState(null);
  const [error, setError] = useState(null);
  const [added, setAdded] = useState(false);

  // Use DB lead data if available, otherwise use property/lookup data
  const data = dbLead || lookupData || property;

  // Lookup property details
  const handleLookup = async () => {
    if (!property?.address) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${PROPERTY_API_URL}/lookup?address=${encodeURIComponent(property.address)}&solar=true`,
        { headers: { 'X-API-Key': PROPERTY_API_KEY } }
      );

      const result = await response.json();
      if (result.success) {
        // Convert API response to display format
        setLookupData({
          address: result.addressFormatted || property.address,
          streetAddress: result.addressLine1,
          city: result.city,
          state: result.state,
          zip: result.zipCode,
          lat: result.latitude,
          lng: result.longitude,
          ownerName: result.owner?.names?.join(', '),
          ownerOccupied: result.owner?.ownerOccupied,
          assessedValue: result.valuation?.assessedValue,
          sqft: result.property?.squareFootage,
          yearBuilt: result.property?.yearBuilt,
          bedrooms: result.property?.bedrooms,
          bathrooms: result.property?.bathrooms,
          lotSize: result.property?.lotSize,
          propertyType: result.property?.propertyType,
          roofSqft: result.roofEstimate?.roofSquareFootage,
          roofSqftMeasured: result.roofEstimate?.roofSquareFootageMeasured,
          roofPitch: result.roofEstimate?.avgPitchDegrees,
          roofSegments: result.roofEstimate?.segmentCount,
          roofAge: result.roofEstimate?.roofAge,
          roofType: result.features?.roofType,
          needsRoofReplacement: result.roofEstimate?.needsReplacement,
          roofCostAsphalt: result.roofEstimate?.costEstimates?.asphalt?.estimate,
          roofCostMetal: result.roofEstimate?.costEstimates?.metal?.estimate,
          roofCostTile: result.roofEstimate?.costEstimates?.tile?.estimate,
          enriched: true
        });

        if (onLookupDetails) {
          onLookupDetails(result);
        }
      } else {
        setError(result.error || 'Lookup failed');
      }
    } catch (e) {
      setError(e.message);
    }

    setLoading(false);
  };

  // Add to CRM
  const handleAddToCRM = async () => {
    const leadData = lookupData || property;
    if (!leadData) return;

    setLoading(true);
    try {
      if (onAddToCRM) {
        await onAddToCRM(leadData);
        setAdded(true);
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  // Generate flyer
  const handleGenerateFlyer = () => {
    const leadData = dbLead || lookupData || property;
    if (onGenerateFlyer && leadData) {
      onGenerateFlyer(leadData);
    }
  };

  if (!property) return null;

  return (
    <div className="property-modal-overlay" onClick={onClose}>
      <div className="property-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>

        {/* Header */}
        <div className="modal-header">
          <div className="modal-address">
            <h2>{data?.streetAddress || data?.street_address || property.address?.split(',')[0]}</h2>
            <p>{data?.city || property.city}, {data?.state || property.state} {data?.zip || data?.zip_code || property.zip}</p>
          </div>
          <div className="modal-badges">
            {(isInDB || dbLead) && <span className="badge in-db">In CRM</span>}
            {(data?.enriched || dbLead?.enriched) && <span className="badge enriched">Enriched</span>}
            {data?.roofSqftMeasured && <span className="badge measured">Satellite</span>}
            {data?.needsRoofReplacement && <span className="badge needs-roof">Needs Roof</span>}
          </div>
        </div>

        {/* Owner Info */}
        {(data?.ownerName || data?.owner_name) && (
          <div className="modal-section owner-section">
            <div className="owner-icon">👤</div>
            <div className="owner-info">
              <span className="owner-name">{data.ownerName || data.owner_name}</span>
              {(data?.ownerOccupied || data?.owner_occupied) && (
                <span className="owner-occupied">Owner Occupied</span>
              )}
            </div>
          </div>
        )}

        {/* Property Details */}
        <div className="modal-section">
          <h3>Property Details</h3>
          <div className="details-grid">
            {(data?.assessedValue || data?.assessed_value) && (
              <div className="detail-item">
                <span className="detail-icon">💰</span>
                <span className="detail-label">Value</span>
                <span className="detail-value">{formatCurrency(data.assessedValue || data.assessed_value)}</span>
              </div>
            )}
            {data?.sqft && (
              <div className="detail-item">
                <span className="detail-icon">📐</span>
                <span className="detail-label">Sqft</span>
                <span className="detail-value">{formatNumber(data.sqft)}</span>
              </div>
            )}
            {(data?.yearBuilt || data?.year_built) && (
              <div className="detail-item">
                <span className="detail-icon">🏠</span>
                <span className="detail-label">Built</span>
                <span className="detail-value">{data.yearBuilt || data.year_built}</span>
              </div>
            )}
            {data?.bedrooms && (
              <div className="detail-item">
                <span className="detail-icon">🛏️</span>
                <span className="detail-label">Beds</span>
                <span className="detail-value">{data.bedrooms}</span>
              </div>
            )}
            {data?.bathrooms && (
              <div className="detail-item">
                <span className="detail-icon">🚿</span>
                <span className="detail-label">Baths</span>
                <span className="detail-value">{data.bathrooms}</span>
              </div>
            )}
            {(data?.lotSize || data?.lot_size) && (
              <div className="detail-item">
                <span className="detail-icon">🌳</span>
                <span className="detail-label">Lot</span>
                <span className="detail-value">{formatNumber(data.lotSize || data.lot_size)} sqft</span>
              </div>
            )}
          </div>
        </div>

        {/* Roof Data */}
        {(data?.roofSqft || data?.roof_sqft || data?.roofCostAsphalt || data?.roof_cost_low) && (
          <div className="modal-section roof-section">
            <h3>
              Roof Estimate
              {(data?.roofSqftMeasured || data?.roof_sqft_measured) && (
                <span className="satellite-badge">📡 Satellite Measured</span>
              )}
            </h3>
            <div className="roof-details">
              <div className="roof-stat">
                <span className="roof-label">Size</span>
                <span className="roof-value">{formatNumber(data.roofSqft || data.roof_sqft)} sqft</span>
              </div>
              {(data?.roofPitch || data?.roof_pitch_degrees) && (
                <div className="roof-stat">
                  <span className="roof-label">Pitch</span>
                  <span className="roof-value">{(data.roofPitch || data.roof_pitch_degrees)?.toFixed(1)}°</span>
                </div>
              )}
              {(data?.roofSegments || data?.roof_segment_count) && (
                <div className="roof-stat">
                  <span className="roof-label">Segments</span>
                  <span className="roof-value">{data.roofSegments || data.roof_segment_count}</span>
                </div>
              )}
              {(data?.roofAge || data?.roof_age) && (
                <div className="roof-stat">
                  <span className="roof-label">Age</span>
                  <span className="roof-value">{data.roofAge || data.roof_age} years</span>
                </div>
              )}
              {(data?.roofType || data?.roof_type) && (
                <div className="roof-stat">
                  <span className="roof-label">Type</span>
                  <span className="roof-value">{data.roofType || data.roof_type}</span>
                </div>
              )}
            </div>

            {/* Cost Estimates */}
            {(data?.roofCostAsphalt || data?.roof_cost_low) && (
              <div className="cost-estimates">
                <div className="cost-item highlight">
                  <span className="cost-label">Asphalt Shingles</span>
                  <span className="cost-value">{formatCurrency(data.roofCostAsphalt || data.roof_cost_low)}</span>
                </div>
                {(data?.roofCostMetal || data?.roof_cost_high) && (
                  <div className="cost-item">
                    <span className="cost-label">Metal Roof</span>
                    <span className="cost-value">{formatCurrency(data.roofCostMetal || data.roof_cost_high)}</span>
                  </div>
                )}
                {data?.roofCostTile && (
                  <div className="cost-item">
                    <span className="cost-label">Tile Roof</span>
                    <span className="cost-value">{formatCurrency(data.roofCostTile)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="modal-error">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="modal-actions">
          {!lookupData && !dbLead?.enriched && (
            <button
              className="action-btn primary"
              onClick={handleLookup}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-small"></span>
                  Looking up...
                </>
              ) : (
                <>🔍 Lookup Details</>
              )}
            </button>
          )}

          {lookupData && !dbLead?.enriched && (
            <button
              className="action-btn refresh"
              onClick={handleLookup}
              disabled={loading}
            >
              🔄 Refresh
            </button>
          )}

          {!isInDB && !added && (
            <button
              className="action-btn success"
              onClick={handleAddToCRM}
              disabled={loading || (!lookupData && !property)}
            >
              {added ? '✓ Added' : '➕ Add to CRM'}
            </button>
          )}

          {added && (
            <button className="action-btn success" disabled>
              ✓ Added to CRM
            </button>
          )}

          {(lookupData || dbLead?.enriched || data?.enriched) && (
            <button
              className="action-btn flyer"
              onClick={handleGenerateFlyer}
            >
              📄 Generate Flyer
            </button>
          )}
        </div>

        {/* Quick tip for non-enriched */}
        {!lookupData && !dbLead?.enriched && !data?.enriched && (
          <div className="modal-tip">
            💡 Click "Lookup Details" to get owner info, roof measurements, and cost estimates
          </div>
        )}
      </div>
    </div>
  );
}

export default PropertyModal;
